import {CdkDragDrop, transferArrayItem} from '@angular/cdk/drag-drop';
import {Component, Inject, OnInit} from '@angular/core';
import {AbstractControl, FormControl, ValidatorFn} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Actions} from '@ngneat/effects-ng';
import {BaseDialogComponent, SnackBarService} from '@q9elements/ui-kit/common';
import {GridColumn, GridQuery, GridStore} from '@q9elements/ui-kit/components';
import {Q9Validators} from '@q9elements/ui-kit/validators';
import {get, intersectionBy} from 'lodash';
import {catchError, EMPTY, map, OperatorFunction} from 'rxjs';

import {DEFAULT_VIEW_ID} from '../../models/constants';
import {selectView} from '../../models/grid-views.actions';
import {EditGridViewDialogPayload, GridView} from '../../models/interfaces';
import {GridViewsDaoService} from '../../services/grid-views-dao.service';
import {GridViewsService} from '../../services/grid-views.service';

@Component({
  selector: 'edit-grid-view-dialog',
  templateUrl: './edit-view-dialog.component.html',
  styleUrls: ['./edit-view-dialog.component.scss']
})
export class EditViewDialogComponent extends BaseDialogComponent implements OnInit {
  title = this.getTitle();
  nameControl = new FormControl(this.view?.name || '');
  included: GridColumn[] = [];
  available: GridColumn[] = [];
  columnWasDropped = false;

  private columns: GridColumn[];

  constructor(
    private store: GridStore,
    private query: GridQuery,
    private gridViewsService: GridViewsService,
    private snackBarService: SnackBarService,
    private gridViewsDaoService: GridViewsDaoService,
    private actions: Actions,
    @Inject(MAT_DIALOG_DATA) public view: GridView | null,
    protected dialogRef: MatDialogRef<EditViewDialogComponent, EditGridViewDialogPayload>
  ) {
    super();
  }

  ngOnInit(): void {
    this.initControls();
    this.initColumns();
  }

  protected sendSubmitRequest() {
    const payload: Partial<GridView> = {
      type: this.store.storeName,
      name: this.nameControl.value!.trim(),
      columns: this.included.map(c => c.field),
      filters: this.view?.filters || []
    };

    if (this.view?.id) {
      return this.gridViewsDaoService
        .update(this.view.id, payload)
        .pipe(map(view => ({...this.view, ...view})));
    }

    return this.gridViewsDaoService.create(payload);
  }

  override onSubmit() {
    if (!this.isFormValid || this.isSubmitted) {
      this.nameControl.markAsTouched();
      this.columnWasDropped = true;

      return;
    }

    this.error = '';
    this.isSubmitted = true;

    return this.sendSubmitRequest()
      .pipe(this.takeUntilComponentDestroy(), this.handleError())
      .subscribe(view => {
        this.isSubmitted = false;
        this.dialogRef.close(view);
      });
  }

  override handleError(): OperatorFunction<any, any> {
    return catchError(error => {
      const errorMessage = get(error, 'error', error);
      const status = get(error, 'status');

      this.isSubmitted = false;

      if (status === 'grid_views.not_found') {
        this.snackBarService.open(errorMessage);
        this.dialogRef.close();
        this.actions.dispatch(selectView({id: DEFAULT_VIEW_ID}));
        this.gridViewsService.deleteView(this.view?.id!);
      } else {
        this.error = errorMessage;
      }

      return EMPTY;
    });
  }

  get isFormValid() {
    return this.nameControl.valid && this.included.length >= 2;
  }

  drop(event: CdkDragDrop<GridColumn[]>) {
    const {container, previousContainer, previousIndex, currentIndex} = event;

    if (previousContainer === container) {
      return void 0;
    }

    this.columnWasDropped = true;

    transferArrayItem(previousContainer.data, container.data, previousIndex, currentIndex);

    const ordered = intersectionBy(this.columns, container.data, 'field');

    container.data.splice(0, container.data.length, ...ordered);
  }

  private getTitle(): string {
    const title = 'grid_views.edit_view_<action>_title';
    let action: 'create' | 'edit' | 'clone' = 'create';

    if (this.view?.id) {
      action = 'edit';
    } else if (this.view) {
      action = 'clone';
    }

    return title.replace('<action>', action).toUpperCase();
  }

  private initControls() {
    const nameValidators = [
      Q9Validators.required('GRID_VIEWS.EDIT_VIEW_NAME_REQUIRED_MSG'),
      Q9Validators.maxLength(40, 'GRID_VIEWS.EDIT_VIEW_NAME_MAX_MSG'),
      this.isUniqueNameValidator()
    ];

    this.nameControl = new FormControl(this.view?.name || '', nameValidators);
  }

  private initColumns() {
    this.columns = this.query.getColumns();

    for (const column of this.columns) {
      const isLocked = column.locked;
      const isIncluded = this.view?.columns?.includes(column.field);

      if (isLocked || isIncluded) {
        this.included.push(column);
      } else {
        this.available.push(column);
      }
    }
  }

  private isUniqueNameValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const view = this.gridViewsService
        .views()
        .find(({id, name}) => id !== this.view?.id && name === control.value.trim());

      return view ? {nonUnique: 'GRID_VIEWS.EDIT_VIEW_NAME_UNIQUE_MSG'} : null;
    };
  }
}
